import { FileExportArray, FileData, MESH_CATEGORIES, FileIdx } from '../../data/data-interfaces.internal.ts';
import { FileDataSchema, MeshType, MESH_TYPES, ExportStatusDetail, ExportProgressDetail, FileInfoArr } from '../../data/data-interfaces.external.ts';
import { isMeshPart } from './_utils.ts';
import { buildZipExport, ZipExportOptions } from './zipExport.ts';
import { startHistoryBatch, cancelHistoryBatch } from '../../logic/undoHistory.ts';
import { saveFileAs, getFileSaveProvider } from '../fileSave.ts';
import { liveAppData } from '../../store/store.ts';

export interface ExportBuilderOptions {
  /** Default fileType for mesh parts (default: 'stl'). */
  preferredMeshType?: MeshType;
  /** ID used in as-export-status events so listeners can target a specific builder (default: 'default'). */
  exporterId?: string;
}

/** Dispatch an as-export-status event on window. Used by ExportBuilder and its plugins. */
export function dispatchExportStatus(detail: ExportStatusDetail): void {
  window.dispatchEvent(new CustomEvent('as-export-status', { detail }));
}

/** Dispatch an as-export-progress event on window. Emitted once per file inside resolve(). */
export function dispatchExportProgress(detail: ExportProgressDetail): void {
  window.dispatchEvent(new CustomEvent('as-export-progress', { detail }));
}

/**
 * Dispatch an as-data-export event on as-app for unified data_export logging.
 * as-app's listener forwards it to sendLog('data_export', subcategory, …). A
 * local save has a clear done signal (saveFileAs resolving), so we log on the
 * confirmed result rather than a started event; a failure folds into the same
 * event via detail.error.
 */
function dispatchDataExport(detail: { subcategory: string; fileInfo?: FileInfoArr; error?: string }): void {
  document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-data-export', {
    bubbles: true, composed: true, detail,
  }));
}

export interface DownloadOptions extends ZipExportOptions {
  /**
   * Override the download filename (including extension).
   * Single file default: '{fileName}.{fileType}'.
   * Multi-file ZIP default: '{appSource}.zip'.
   */
  filename?: string;
}

export interface ResolveOptions {
  /**
   * Override the fileType used when fetching mesh parts from the configurator.
   * Useful for formats that require a specific type (e.g. '3mf' for GrabCAD)
   * without permanently mutating the builder's file list.
   */
  meshFileType?: MeshType;
}

/**
 * Descriptor for a modal component associated with an ExportBuilder plugin.
 * Registered via ExportBuilder.registerModal() — consumed by as-app to append elements to its overlay.
 */
export interface ExportModalDescriptor {
  /** Custom element tag name (e.g. 'as-modal-grabcad-success'). */
  tagName: string;
  /** id attribute given to the element when appended to the modal overlay. */
  id: string;
  /** If true, sets disable-bg-close on the element so clicking the overlay backdrop won't dismiss it. */
  disableBgClose?: boolean;
}

/**
 * Descriptor for a button component associated with an ExportBuilder plugin.
 * Registered via ExportBuilder.registerButton() — consumed by as-export-panel.
 */
export interface ExportButtonDescriptor {
  /** Custom element tag name (e.g. 'as-grabcad-export-button'). */
  tagName: string;
  /** as-app[env] value required to show this button. Omit for always-visible buttons. */
  requiredEnv?: string;
  /** Attribute prefix for as-export-panel forwarding (e.g. 'grabcad' makes the panel read 'grabcad-default-text'). */
  panelPrefix?: string;
  /** Additional attribute names forwarded as-is from as-export-panel (e.g. 'package-name'). */
  extraAttrs?: string[];
  /** Static attributes set on the button element by as-export-step (e.g. default-text, tooltip-default-text). */
  defaultAttrs?: Record<string, string>;
}

export class ExportBuilder {
  private static plugins: Array<(builder: ExportBuilder) => object> = [];
  private static _buttonDescriptors: ExportButtonDescriptor[] = [];
  private static _modalDescriptors: ExportModalDescriptor[] = [];

  /**
   * Register a plugin to be applied to all new ExportBuilder instances.
   */
  static registerPlugin(plugin: (builder: ExportBuilder) => object): void {
    this.plugins.push(plugin);
  }

  /**
   * Register a button component descriptor to be rendered by as-export-panel.
   * Called by plugin modules alongside registerPlugin().
   */
  static registerButton(descriptor: ExportButtonDescriptor): void {
    this._buttonDescriptors.push(descriptor);
  }

  /** Returns a snapshot of all registered button descriptors. */
  static getButtonDescriptors(): ExportButtonDescriptor[] {
    return [...this._buttonDescriptors];
  }

  /**
   * Register a modal component descriptor to be rendered by as-app inside its modal overlay.
   * Called by plugin modules alongside registerPlugin() / registerButton().
   */
  static registerModal(descriptor: ExportModalDescriptor): void {
    this._modalDescriptors.push(descriptor);
  }

  /** Returns a snapshot of all registered modal descriptors. */
  static getModalDescriptors(): ExportModalDescriptor[] {
    return [...this._modalDescriptors];
  }

  private _files: FileData[] = [];
  private _beforeExportFns = new Map<FileIdx, () => Promise<void>>();
  private _getDataFns      = new Map<FileIdx, () => Promise<ArrayBuffer>>();
  private _counter = 0;
  #preferredMeshType: MeshType;
  #exporterId: string;

  constructor(private appSource: string, opts: ExportBuilderOptions = {}) {
    this.#preferredMeshType = opts.preferredMeshType ?? 'stl';
    this.#exporterId        = opts.exporterId        ?? 'default';

    // Apply registered plugins
    for (const plugin of ExportBuilder.plugins) {
      Object.assign(this, plugin(this));
    }
  }

  get exporterId(): string { return this.#exporterId; }

  get preferredMeshType(): MeshType { return this.#preferredMeshType; }

  set preferredMeshType(v: MeshType) {
    if (!(MESH_TYPES as readonly string[]).includes(v))
      throw new Error(`[ExportBuilder] invalid preferredMeshType: "${v}" — expected one of: ${MESH_TYPES.join(', ')}`);
    this.#preferredMeshType = v;
  }

  /**
   * Clears all files and callbacks from the builder, resetting its state.
   */
  clear(): this {
    this._files = [];
    this._beforeExportFns.clear();
    this._getDataFns.clear();
    this._counter = 0;
    return this;
  }

  private static async _toBuffer(raw: ArrayBuffer | Blob): Promise<ArrayBuffer> {
    return raw instanceof Blob ? raw.arrayBuffer() : raw;
  }

  private _addSchemaItem(input: FileDataSchema, parentIdx?: FileIdx): FileIdx {
    const idx = this._counter++;
    const isMesh = (MESH_CATEGORIES as readonly string[]).includes(input.category);

    let staticData: ArrayBuffer | undefined;
    let dataFn: (() => Promise<ArrayBuffer>) | undefined;

    if (typeof input.data === 'function') {
      const raw = input.data;
      dataFn = async () => ExportBuilder._toBuffer(await raw());
    } else if (input.data instanceof Blob) {
      const blob = input.data;
      dataFn = () => blob.arrayBuffer();
    } else {
      staticData = input.data as ArrayBuffer | undefined;
    }

    this._files.push({
      appSource: this.appSource,
      fileIdx: idx,
      fileKey: input.fileKey ?? crypto.randomUUID(),
      fileName: input.fileName,
      category: input.category,
      fileType: input.fileType ?? (isMesh ? this.#preferredMeshType : input.category === 'brep' ? 'stp' : ''),
      data: staticData ?? new ArrayBuffer(0),
      createdAt: new Date(),
      ...(parentIdx !== undefined && { parent: parentIdx }),
      ...(input.finalize !== undefined && { finalize: input.finalize }),
      ...(input.customData && { customData: input.customData }),
      ...(input.tags && { tags: input.tags }),
    } as FileData);

    if (input.beforeExport) this._beforeExportFns.set(idx, input.beforeExport);
    if (dataFn)             this._getDataFns.set(idx, dataFn);

    for (const child of input.children ?? []) {
      this._addSchemaItem(child, idx);
    }

    return idx;
  }

  /**
   * Add any file to the builder. `data` may be a pre-set ArrayBuffer or an async
   * function that returns one at export time (called after beforeExport, if present).
   * For mesh categories without data, the configurator is auto-fetched at resolve time.
   */
  addFile(input: FileDataSchema, parentIdx?: FileIdx): FileIdx {
    return this._addSchemaItem(input, parentIdx);
  }

  /**
   * Shorthand for addFile with category 'trcadMesh'. Data is auto-fetched from
   * as-configurator at resolve time unless overridden by a data function.
   * Supply beforeExport() to set any configurator params needed before the fetch.
   */
  addConfiguratorMesh(input: Omit<FileDataSchema, 'category' | 'fileType'> & { fileType?: MeshType } = {}): FileIdx {
    return this._addSchemaItem({ category: 'trcadMesh', ...input });
  }

  /**
   * Load a declarative schema into the builder.
   * By default clears first; pass { append: true } to extend instead.
   * Handles both PresetFileSchema and AutoFetchSchema items.
   */
  fromSchema(schema: FileDataSchema[], opts: { append?: boolean } = {}): this {
    if (!opts.append) this.clear();
    for (const item of schema) {
      this._addSchemaItem(item);
    }
    return this;
  }

  /**
   * Overrides the fileType for all files in mesh categories.
   */
  setMeshFileType(type: MeshType): this {
    for (const file of this._files) {
      if (isMeshPart(file)) file.fileType = type;
    }
    return this;
  }

  /**
   * Build and export to a zip archive (Uint8Array).
   * For each auto-fetch entry: runs beforeExport() then fetches data via getData() or as-configurator.
   * All fetches run sequentially. No fileType validation — ZIP accepts any format.
   */
  async toZip(opts: ZipExportOptions = {}): Promise<Uint8Array> {
    const resolvedFiles = await this.resolve();
    return buildZipExport(resolvedFiles as FileExportArray, opts);
  }

  /**
   * Resolve and save the result to disk (environment-appropriate, see
   * logic/fileSave.ts — native save dialog in hosted environments, browser
   * download otherwise).
   * Single file: saves directly with its own filename and extension.
   * Multiple files: packages as a ZIP.
   */
  async download(opts: DownloadOptions = {}): Promise<void> {
    const id = this.#exporterId;
    // A host save provider (e.g. GrabCAD's native save dialog) means the file
    // left through that channel, so tag the data_export with the env — mirroring
    // the import side (as-file-upload). No provider => a plain browser download,
    // tagged 'local'.
    const subcategory = getFileSaveProvider() ? (liveAppData.appEnv as string) : 'local';
    dispatchExportStatus({ exporterId: id, operation: 'download', state: 'pending' });
    try {
      const { filename, ...zipOpts } = opts;
      const resolved = await this.resolve();

      let blob: Blob;
      let name: string;

      if (resolved.length === 1) {
        const file = resolved[0];
        blob = new Blob([file.data]);
        // Strip any extension the caller may have supplied (e.g. '.zip') and use the real one.
        const base = filename ? filename.replace(/\.[^.]+$/, '') : (file.fileName ?? file.fileKey);
        name = `${base}.${file.fileType}`;
      } else {
        const zip = await buildZipExport(resolved as FileExportArray, zipOpts);
        blob = new Blob([zip], { type: 'application/zip' });
        name = filename ?? `${this.appSource}.zip`;
      }

      const result = await saveFileAs(blob, name);
      if (!result.success) {
        // User cancelled the save dialog — nothing left the app, so no data_export log.
        dispatchExportStatus({ exporterId: id, operation: 'download', state: 'error', message: 'cancelled' });
        return;
      }

      dispatchExportStatus({ exporterId: id, operation: 'download', state: 'success' });
      // Unified data-export log: a save is a data_export, tagged with the channel
      // it went through. fileInfo carries each resolved part's size + type.
      dispatchDataExport({
        subcategory,
        fileInfo: resolved.map((f) => ({ sizeBytes: f.data.byteLength, fileType: f.fileType })),
      });
    } catch (err: any) {
      dispatchExportStatus({ exporterId: id, operation: 'download', state: 'error', message: err.message });
      // Fold the failure into the same data_export log via detail.error.
      dispatchDataExport({ subcategory, error: err.message });
      throw err;
    }
  }

  /**
   * Materialise all file data: runs beforeExport hooks, calls data functions, and
   * auto-fetches from as-configurator for trcadMesh / workpiece / brep entries with no data.
   * Sequential — each file resolves before the next begins.
   */
  async resolve(opts: ResolveOptions = {}): Promise<FileData[]> {
    const resolved: FileData[] = [];
    const total = this._files.length;
    let filesDone = 0;

    startHistoryBatch('export');
    window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'export', busy: true } }));
    dispatchExportProgress({ exporterId: this.exporterId, current: 0, total });
    try {
    for (const file of this._files) {
      const fileType = (opts.meshFileType && isMeshPart(file)) ? opts.meshFileType : file.fileType;

      if (file.data.byteLength === 0 && (this._beforeExportFns.has(file.fileIdx) || this._getDataFns.has(file.fileIdx) || isMeshPart(file) || file.category === 'brep')) {
        const beforeExport = this._beforeExportFns.get(file.fileIdx);
        if (beforeExport) await beforeExport();

        const getDataFn = this._getDataFns.get(file.fileIdx);
        if (getDataFn) {
          resolved.push({ ...file, fileType, data: await getDataFn() } as FileData);
        } else if (isMeshPart(file) || file.category === 'brep') {
          const cfgr = document.querySelector('as-configurator') as any;
          if (!cfgr)
            throw new Error(
              `[ExportBuilder] as-configurator not found — needed to fetch mesh for "${
                file.fileName ?? file.fileKey
              }"`
            );

          const finalize = file.finalize ?? true;
          const blob = await cfgr.getMesh(fileType, file.fileName ?? file.fileKey, finalize);
          resolved.push({ ...file, fileType, data: await blob.arrayBuffer() } as FileData);
        } else {
          resolved.push({ ...file, fileType, data: file.data.slice(0) } as FileData);
        }
      } else {
        // Slice the ArrayBuffer so postMessage transfers cannot detach _files data,
        // keeping the builder reusable after a GrabCAD send (or any transfer op).
        resolved.push({ ...file, fileType, data: file.data.slice(0) } as FileData);
      }
      dispatchExportProgress({ exporterId: this.exporterId, current: ++filesDone, total });
    }
    } finally {
      cancelHistoryBatch();
      window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'export', busy: false } }));
    }

    return resolved;
  }

  /** Return the raw internal file list without resolving data. Useful for inspection; prefer resolve() for export-ready data. */
  build(): FileExportArray {
    return [...this._files] as FileExportArray;
  }

  /** Log and return a human-readable summary of the current file list. */
  print(): string {
    const lines = [`ExportBuilder [${this.appSource}] preferredMeshType=${this.#preferredMeshType} — ${this._files.length} file(s)`];
    for (const file of this._files) {
      const parentStr = file.parent !== undefined ? ` → parent:${file.parent}` : '';
      const dataStr    = file.data.byteLength > 0 ? ` [${file.data.byteLength}b]` : ' [auto-fetch]';
      const beforeStr  = this._beforeExportFns.has(file.fileIdx) ? ' +beforeExport' : '';
      const dataFnStr  = this._getDataFns.has(file.fileIdx)      ? ' +data()'       : '';
      lines.push(`  [${file.fileIdx}] ${file.category}/${file.fileType} "${file.fileName ?? file.fileKey}"${parentStr}${dataStr}${beforeStr}${dataFnStr}`);
    }
    const output = lines.join('\n');
    console.log(output);
    return output;
  }
}
